import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send, Mic, MicOff, Loader2, Paperclip } from "lucide-react";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onUserMessage?: () => void;
  isLoading: boolean;
  darkTheme: boolean;
  voiceRecognition: {
    isSupported: boolean;
    isListening: boolean;
    interimTranscript: string;
    startListening: () => void;
    stopListening: () => void;
    resetTranscript: () => void;
  };
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  pollType?: "date" | "form" | "availability" | "quizz";
  className?: string;
  disabled?: boolean;
  disabledReason?: "guest" | "limit";
  hasPendingProposal?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  onUserMessage,
  isLoading,
  darkTheme,
  voiceRecognition,
  textareaRef,
  pollType = "date",
  className,
  disabled = false,
  disabledReason = "limit",
  hasPendingProposal = false,
}) => {
  const { t } = useTranslation(["creator", "common", "errors"]);
  const idlePlaceholder = hasPendingProposal
    ? pollType === "form"
      ? t("creator:chat_input.placeholder_modify_or_validate_form")
      : t("creator:chat_input.placeholder_modify_or_validate_poll")
    : pollType === "form"
      ? t("creator:chat_input.placeholder_describe_form")
      : t("creator:chat_input.placeholder_describe_poll");
  return (
    <div
      className={`
      flex items-center 
      gap-2 
      md:gap-3 
      rounded-full 
      border ${
        darkTheme
          ? "bg-[#0a0a0a] border-gray-800"
          : "bg-white border-gray-200 shadow-lg"
      }`}
    >
      <textarea
        ref={textareaRef}
        data-testid="chat-input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          // Notify parent on typing to allow mobile auto-toggle to Chat
          if (typeof onUserMessage === "function") onUserMessage();
        }}
        onFocus={() => {
          // Notify parent on focus as well (helps when Preview is visible on mobile)
          if (typeof onUserMessage === "function") onUserMessage();
        }}
        onKeyDown={onKeyPress}
        placeholder={
          disabled
            ? disabledReason === "guest"
              ? t("creator:chat_input.placeholder_sign_in_to_use_ai")
              : t("creator:chat_input.placeholder_credit_limit_reached")
            : isLoading
              ? t("creator:chat_input.placeholder_generating")
              : idlePlaceholder
        }
        disabled={isLoading || disabled}
        className={`flex-1 min-w-0 resize-none border-0 px-2 md:px-4 py-2 focus:outline-none min-h-[44px] max-h-48 text-sm md:text-base bg-transparent transition-all duration-200 ${
          darkTheme
            ? "text-white placeholder-gray-400"
            : "text-gray-900 placeholder-gray-500"
        } ${isLoading || disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        rows={1}
        style={{
          fieldSizing: "content",
          overflow: "hidden",
        }}
      />

      {/* Bouton micro pour reconnaissance vocale */}
      {voiceRecognition.isSupported && (
        <button
          onClick={() => {
            if (voiceRecognition.isListening) {
              voiceRecognition.stopListening();
            } else {
              voiceRecognition.resetTranscript();
              voiceRecognition.startListening();
            }
          }}
          disabled={isLoading || disabled}
          className={`
                rounded-full p-2 transition-all flex-shrink-0
                ${
                  isLoading || disabled
                    ? "bg-transparent text-gray-500 cursor-not-allowed opacity-60"
                    : voiceRecognition.isListening
                      ? "bg-red-500 text-white hover:bg-red-600 animate-pulse"
                      : darkTheme
                        ? "bg-transparent text-gray-300 hover:bg-gray-700"
                        : "bg-transparent text-gray-600 hover:bg-gray-100"
                }
              `}
          title={
            voiceRecognition.isListening
              ? t("creator:chat_input.stop_listening")
              : t("creator:chat_input.enable_microphone")
          }
          aria-label={
            voiceRecognition.isListening
              ? t("creator:chat_input.stop_listening")
              : t("creator:chat_input.enable_microphone")
          }
          data-testid="chatinput-button"
        >
          {voiceRecognition.isListening ? (
            <MicOff className="w-4 h-4 md:w-5 md:h-5" />
          ) : (
            <Mic className="w-4 h-4 md:w-5 md:h-5" />
          )}
        </button>
      )}

      <button
        data-testid="send-message-button"
        onClick={onSend}
        disabled={isLoading || !value.trim() || disabled}
        className={`
              rounded-full p-2 transition-all flex-shrink-0
              ${
                isLoading || !value.trim() || disabled
                  ? "bg-transparent text-gray-500 cursor-not-allowed"
                  : darkTheme
                    ? "bg-transparent text-gray-300 hover:bg-gray-700"
                    : "bg-transparent text-gray-600 hover:bg-gray-100"
              }
            `}
        title={
          isLoading
            ? t("creator:chat_input.placeholder_generating")
            : t("creator:chat_input.send")
        }
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
        ) : (
          <Send className="w-4 h-4 md:w-5 md:h-5" />
        )}
      </button>
    </div>
  );
};
